import React, { memo, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Map, OrderedMap } from 'immutable'
import { formatPrice, getTotal, getSize } from '@aytm/app/swidget/questions/components/sh/functions'

import ecommerceStyle from '@aytm/app/swidget/questions/styles/sh/ecommerce.module.scss'
import popupStyle from '@aytm/app/swidget/questions/styles/sh/popup.module.scss'
import Notice from '@aytm/app/swidget/questions/components/common/notice'
import { asBoolean } from '@aytm/lib/immutable/ImmutableModel'
import Button from '@aytm/lib/rfd-ui/components/basic/button'
import { stopEvent } from '@aytm/lib/common/func'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects'

Ecommerce.propTypes = {
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  showErrors: PropTypes.bool,
  mobile: PropTypes.bool,
  answer: PropTypes.instanceOf(Map).isRequired,
  locale: PropTypes.instanceOf(Map).isRequired,
  answers: PropTypes.instanceOf(OrderedMap).isRequired,
  onAction: PropTypes.func,
  onProductClick: PropTypes.func
}

export function Ecommerce(props) {
  const { showErrors, locale, answers, onProductClick, questionSchema, answer, onAction, mobile } = props
  const findability =
    'find' == questionSchema.get('mode_type') ||
    (1 == questionSchema.get('max_cart_size') && !questionSchema.get('shopping_cart'))
  const maxQty = findability ? null : questionSchema.get('max_qty_per_product')
  const maxCartSize = findability ? null : questionSchema.get('max_cart_size')
  const maxMoney = findability ? null : questionSchema.get('max_money_spent')
  const productsPerRow = questionSchema.get('products_per_row') || 3

  const onButtonClick = (event, item, disabled) => {
    stopEvent(event)
    if (!disabled) {
      onAction(item, 1)
      hapticEffects.selection()
    }
  }

  return (
    <div className={ecommerceStyle.container} data-testid="SW-SH-ECommerceView">
      {findability ? (
        ''
      ) : (
        <Notice
          className={ecommerceStyle.notice}
          showErrors={showErrors}
          text={locale.getIn(['sh', 'amazonNote'])}
          data-testid="SW-SH-ValidationMessage"
        />
      )}
      <div className={ecommerceStyle.ecommerce} data-testid="SW-SH-Items">
        {answers
          .filter(answer => !answer.get('na'))
          .toArray()
          .map((item, i) => {
            const total = getTotal(answer)
            const itemPrice = item.get('discounted_price') || item.get('price')
            const answerQty = answer.getIn(['answers', item.get('id'), 'count'])
            const disabled =
              (asBoolean(maxQty) && maxQty <= answerQty) ||
              (asBoolean(maxCartSize) && maxCartSize <= getSize(answer)) ||
              (asBoolean(maxMoney) && maxMoney < total + itemPrice)

            return (
              <div
                className={`${ecommerceStyle.item} ${ecommerceStyle['divided' + productsPerRow]} ${
                  item.get('available') ? ecommerceStyle.active : ''
                } ${mobile ? ecommerceStyle.mobile : ''}`}
                key={'item' + i}
                data-testid="SW-SH-Item"
                onClick={item.get('available') ? () => onProductClick(item) : null}
              >
                <div
                  data-testid="SW-SH-ECommerceImage"
                  className={ecommerceStyle.imageContainer}
                  style={{
                    backgroundImage: item.getIn(['image', 'original_url'])
                      ? `url(${item.getIn(['image', 'original_url'])})`
                      : `url('/images/widget/sh/no-image.jpg')`
                  }}
                ></div>

                {/*<div className={ecommerceStyle.text} dangerouslySetInnerHTML={{ __html: item.get('description') }} />*/}
                {item.get('available') && (
                  <Fragment>
                    <div
                      data-testid="SW-SH-Item-Header"
                      className={ecommerceStyle.header}
                      dangerouslySetInnerHTML={{ __html: item.get('text') }}
                    />
                    {item.get('price') != item.get('discounted_price') &&
                    item.get('discounted_price') &&
                    !questionSchema.get('hide_price') ? (
                      <div
                        className={ecommerceStyle.discountedPrice}
                        data-testid="SW-SH-Item-DPrice"
                        dangerouslySetInnerHTML={{
                          __html: formatPrice(item.get('price'), questionSchema.get('price_mask'), true)
                        }}
                      />
                    ) : (
                      ''
                    )}
                    {!questionSchema.get('hide_price') && (
                      <div
                        className={ecommerceStyle.price}
                        data-testid="SW-SH-Item-Price"
                        dangerouslySetInnerHTML={{
                          __html: formatPrice(
                            item.get('discounted_price') ? item.get('discounted_price') : item.get('price'),
                            questionSchema.get('price_mask'),
                            true
                          )
                        }}
                      />
                    )}
                    <Button
                      label={locale.getIn(['sh', 'addToCart'])}
                      onClick={event => onButtonClick(event, item, disabled)}
                      className={`${popupStyle.button} ${disabled ? popupStyle.disbledButton : ''}`}
                      data-testid="SW-SH-Item-AddButton"
                      disabled={disabled}
                    />
                  </Fragment>
                )}
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default memo(
  Ecommerce,
  (prevProps, nextProps) =>
    prevProps.showErrors === nextProps.showErrors &&
    prevProps.locale === nextProps.locale &&
    prevProps.answers === nextProps.answers &&
    prevProps.answer === nextProps.answer &&
    prevProps.mobile === nextProps.mobile &&
    prevProps.onProductClick === nextProps.onProductClick &&
    prevProps.questionSchema === nextProps.questionSchema
)
